import { uuidModule } from "@/modules/uuid";
import {
  BaseSyncOperation,
  BaseSyncOperationParams,
} from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { ILinkNoteToCalendarEventOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncCustomErrorData, SyncModelData } from "@/store/sync/types";
import { resolveSourceIdForCalendarEvent } from "@/modules/uuid/sync-models/resolveSourceIdForCalendarEvent";
import { resolveNoteSourceSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteSourceSyncModelUuid";
import { SourceModelData, SourceUpsertedSyncUpdateValue } from "@/store/sources/types";
import { NoteSourceModelData, NoteSourceUpsertedSyncUpdateValue } from "@/store/sources/types";
import { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { Uuid } from "@/domains/global/identifiers";
import { UNTITLED_CALENDAR_EVENT_TITLE } from "@/domains/untitled/untitled";

export class LinkNoteToCalendarEventOperation extends BaseSyncOperation<ILinkNoteToCalendarEventOperation> {
  private calendarEvent?: SpaceAccountCalendarEventObservable;
  private readonly spaceAccountCalendarEventId: Uuid;

  constructor(
    params: BaseSyncOperationParams<ILinkNoteToCalendarEventOperation> & {
      space_account_calendar_event_id: Uuid;
    }
  ) {
    super(params);
    this.spaceAccountCalendarEventId = params.space_account_calendar_event_id;
  }

  get operationKind(): "LINK_NOTE_TO_CALENDAR_EVENT" {
    return "LINK_NOTE_TO_CALENDAR_EVENT";
  }

  async execute() {
    this.calendarEvent = await this.store.spaceAccountCalendarEvents.getAsync(
      this.spaceAccountCalendarEventId
    );

    await super.execute();
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const syncUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];

    // 1. Create the source sync model
    const sourceId = resolveSourceIdForCalendarEvent({
      connectedCalendarEventId: this.payload.connected_calendar_event_id,
    });
    const sourceValue: SourceUpsertedSyncUpdateValue = {
      model_id: sourceId,
      model_kind: "SOURCE",
      model_version: 0,
      model_data: {
        id: sourceId,
        source_kind: "CALENDAR_EVENT",
        associated_artifact_id: this.payload.connected_calendar_event_id,
        primary_label: this.calendarEvent?.primaryLabel || UNTITLED_CALENDAR_EVENT_TITLE,
        secondary_label: null,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const sourceUpserted: OptimisticSyncUpdate<SourceModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: sourceValue,
    };
    syncUpdates.push(sourceUpserted);

    // 2. Create the note_source sync model
    const noteSourceId = resolveNoteSourceSyncModelUuid({
      noteId: this.payload.note_id,
      sourceId,
    });
    const noteSourceValue: NoteSourceUpsertedSyncUpdateValue = {
      model_id: noteSourceId,
      model_kind: "NOTE_SOURCE",
      model_version: 0,
      model_data: {
        id: noteSourceId,
        note_id: this.payload.note_id,
        source_id: sourceId,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const noteSourceUpserted: OptimisticSyncUpdate<NoteSourceModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: noteSourceValue,
    };
    syncUpdates.push(noteSourceUpserted);

    return syncUpdates;
  }

  async triggerRecompute() {
    const sourceId = resolveSourceIdForCalendarEvent({
      connectedCalendarEventId: this.payload.connected_calendar_event_id,
    });
    await this.store.sources.recompute(sourceId);

    const noteSourceId = resolveNoteSourceSyncModelUuid({
      noteId: this.payload.note_id,
      sourceId,
    });
    await this.store.noteSources.recompute(noteSourceId);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.ignoreError(SyncErrorHandlingType.RetryWithLimit);
  }
}
